/**
 * Authentication API Service
 * Handles all authentication-related API calls
 */

import httpClient from './httpClient';

/**
 * Authentication API Service Class
 */
class AuthApiService {
  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.username - User username/email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response
   */
  async login(credentials) {
    try {
      const response = await httpClient.post('/auth/login', credentials);
      
      // Handle the API response structure
      if (response.responseData) {
        return response.responseData;
      }
      
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Logout user
   * @returns {Promise<Object>} Logout response
   */
  async logout() {
    try {
      const response = await httpClient.post('/auth/logout');
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh authentication token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} Token refresh response
   */
  async refreshToken(refreshToken) {
    try {
      const response = await httpClient.post('/auth/refresh', {
        refreshToken,
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send OTP to email for registration
   * @param {string} email - User email
   * @returns {Promise<Object>} OTP send response
   */
  async sendRegistrationOTP(email) {
    try {
      const response = await httpClient.post('/auth/send-registration-otp', {
        email,
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify OTP for registration
   * @param {Object} otpData - OTP verification data
   * @param {string} otpData.email - User email
   * @param {string} otpData.otp - OTP code
   * @returns {Promise<Object>} OTP verification response
   */
  async verifyRegistrationOTP(otpData) {
    try {
      const response = await httpClient.post('/auth/verify-registration-otp', otpData);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Register new user after OTP verification
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    try {
      const response = await httpClient.post('/auth/register', userData);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Password reset response
   */
  async requestPasswordReset(email) {
    try {
      const response = await httpClient.post('/auth/forgot-password', {
        email,
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Reset password
   * @param {Object} resetData - Password reset data
   * @param {string} resetData.token - Reset token
   * @param {string} resetData.password - New password
   * @returns {Promise<Object>} Password reset response
   */
  async resetPassword(resetData) {
    try {
      const response = await httpClient.post('/auth/reset-password', resetData);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Change password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password
   * @returns {Promise<Object>} Password change response
   */
  async changePassword(passwordData) {
    try {
      const response = await httpClient.post(
        '/auth/change-password',
        passwordData
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile response
   */
  async getCurrentUser() {
    try {
      const response = await httpClient.get('/auth/profile');
      
      // Handle the API response structure
      if (response.responseData) {
        return response.responseData;
      }
      
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get user permissions (admin only)
   * @param {number} userId
   */
  async getUserPermissions(userId) {
    try {
      const response = await httpClient.get(`/users/${userId}/permissions`);
      return response.responseData ?? response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user permissions (admin only)
   * @param {number} userId
   * @param {string[]} permissions
   */
  async updateUserPermissions(userId, permissions) {
    try {
      const response = await httpClient.put(`/users/${userId}/permissions`, permissions);
      return response.responseData ?? response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Update response
   */
  async updateProfile(userData) {
    try {
      const response = await httpClient.put('/auth/profile', userData);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify email address
   * @param {string} token - Verification token
   * @returns {Promise<Object>} Verification response
   */
  async verifyEmail(token) {
    try {
      const response = await httpClient.post('/auth/verify-email', {
        token,
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Resend email verification
   * @param {string} email - User email
   * @returns {Promise<Object>} Resend response
   */
  async resendEmailVerification(email) {
    try {
      const response = await httpClient.post('/auth/resend-verification', {
        email,
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Check if email is available
   * @param {string} email - Email to check
   * @returns {Promise<Object>} Availability response
   */
  async checkEmailAvailability(email) {
    try {
      const response = await httpClient.get(
        `/auth/check-email?email=${encodeURIComponent(email)}`
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Handle authentication errors
   * @param {Error} error - Original error
   * @returns {Error} Processed error
   */
  handleAuthError(error) {
    // Extract meaningful error message
    const message =
      error.response?.data?.responseMessage ||
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Authentication failed';

    // Create new error with processed message
    const authError = new Error(message);
    authError.status = error.response?.status;
    authError.code = error.response?.data?.errorCode || error.response?.data?.code;
    authError.originalError = error;

    return authError;
  }

  /**
   * Mock login for development/testing
   * @param {Object} credentials - User credentials
   * @returns {Promise<Object>} Mock login response
   */
  async mockLogin(credentials) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Mock successful login
    if (
      credentials.email === 'admin@example.com' &&
      credentials.password === 'password'
    ) {
      return {
        data: {
          user: {
            id: '1',
            name: 'Admin User',
            email: 'admin@example.com',
            role: 'ADMIN',
            permissions: [
              'dashboard:view',
              'vendors:view',
              'vendors:create',
              'vendors:edit',
              'vendors:delete',
              'customers:view',
              'customers:create',
              'customers:edit',
              'customers:delete',
              'purchases:view',
              'purchases:create',
              'purchases:edit',
              'purchases:delete',
              'sales:view',
              'sales:create',
              'sales:edit',
              'sales:delete',
              'expenses:view',
              'expenses:create',
              'expenses:edit',
              'expenses:delete',
              'reports:view',
              'reports:export',
              'settings:view',
              'settings:edit',
              'users:view',
              'users:create',
              'users:edit',
              'users:delete',
              'users:activate',
              'users:deactivate',
            ],
          },
          token: 'mock-jwt-token-' + Date.now(),
          refreshToken: 'mock-refresh-token-' + Date.now(),
        },
      };
    }

    // Mock failed login
    throw new Error('Invalid email or password');
  }
}

// Create and export service instance
export const authApiService = new AuthApiService();
export default authApiService;
